import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { User, Mail, Lock, CreditCard, Eye, EyeOff, Save, RefreshCw, CheckCircle, Shield } from 'lucide-react';
import { toast } from 'sonner';

export default function AccountPage() {
  const { api, user } = useAuth();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [oldPw, setOldPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    if (user) {
      setEmail(user.email || '');
      setName(user.name || user.google_name || '');
    }
    (async () => {
      try {
        const r = await api('get', '/api/platform/account-info');
        setSubscription(r.data.subscription);
      } catch {}
    })();
  }, [user, api]);

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api('post', '/api/platform/account-info', { name, email });
      toast.success('Accountgegevens bijgewerkt');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Opslaan mislukt');
    }
    setSaving(false);
  };

  const changePw = async (e) => {
    e.preventDefault();
    if (newPw.length < 8) { toast.error('Nieuw wachtwoord moet minimaal 8 tekens zijn'); return; }
    setSavingPw(true);
    try {
      await api('post', '/api/platform/change-password', { old_password: oldPw, new_password: newPw });
      toast.success('Wachtwoord gewijzigd');
      setOldPw(''); setNewPw('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Wachtwoord wijzigen mislukt');
    }
    setSavingPw(false);
  };

  const ROLE_LABELS = {
    radar_free: 'Gratis',
    radar_paid: 'IMPERIA AI Radar',
    beheer_paid: 'IMPERIA AI Beheer',
    admin: 'Administrator',
  };

  return (
    <div data-testid="account-page" style={{ maxWidth: '700px' }}>
      <h1 style={{ fontFamily: "'Sora', sans-serif", fontSize: '22px', fontWeight: 800, color: '#0f172a', marginBottom: '4px' }}>
        Account
      </h1>
      <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '24px' }}>
        Beheer uw accountgegevens, wachtwoord en abonnement
      </p>

      {/* Profile */}
      <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #f1f5f9', padding: '24px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <User size={16} style={{ color: '#D4A017' }} />
          <h2 style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>Persoonlijke gegevens</h2>
        </div>
        <div className="form-grid-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '6px' }}>Naam</label>
            <input value={name} onChange={e => setName(e.target.value)} className="rc-input" data-testid="account-name" />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '6px' }}>E-mailadres</label>
            <input value={email} onChange={e => setEmail(e.target.value)} type="email" className="rc-input" data-testid="account-email" />
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {user?.auth_provider === 'google' && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#f0fdf4', color: '#16a34a', fontSize: '11px', padding: '3px 8px', borderRadius: '6px', fontWeight: 600 }}>
                <CheckCircle size={11} /> Google account
              </span>
            )}
          </div>
          <button onClick={saveProfile} disabled={saving} data-testid="save-account"
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 20px', borderRadius: '10px', border: 'none', background: '#0f172a', color: 'white', fontSize: '13px', fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
            {saving ? <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={14} />} Opslaan
          </button>
        </div>
      </div>

      {/* Password */}
      <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #f1f5f9', padding: '24px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <Lock size={16} style={{ color: '#D4A017' }} />
          <h2 style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>Wachtwoord wijzigen</h2>
        </div>
        <form onSubmit={changePw}>
          <div className="form-grid-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '6px' }}>Huidig wachtwoord</label>
              <div style={{ position: 'relative' }}>
                <input type={showOld ? 'text' : 'password'} value={oldPw} onChange={e => setOldPw(e.target.value)} className="rc-input" style={{ paddingRight: '40px' }} />
                <button type="button" onClick={() => setShowOld(!showOld)} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
                  {showOld ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '6px' }}>Nieuw wachtwoord</label>
              <div style={{ position: 'relative' }}>
                <input type={showNew ? 'text' : 'password'} value={newPw} onChange={e => setNewPw(e.target.value)} className="rc-input" style={{ paddingRight: '40px' }} />
                <button type="button" onClick={() => setShowNew(!showNew)} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
                  {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
          </div>
          <button type="submit" disabled={savingPw || !oldPw || !newPw} data-testid="change-password"
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 20px', borderRadius: '10px', border: '1px solid #e2e8f0', background: 'white', color: '#374151', fontSize: '13px', fontWeight: 600, cursor: 'pointer', opacity: (!oldPw || !newPw) ? 0.5 : 1 }}>
            <Shield size={14} /> Wachtwoord wijzigen
          </button>
        </form>
      </div>

      {/* Subscription */}
      <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #f1f5f9', padding: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <CreditCard size={16} style={{ color: '#D4A017' }} />
          <h2 style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>Abonnement & Betaling</h2>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f8fafc' }}>
            <span style={{ fontSize: '13px', color: '#64748b' }}>Huidig plan</span>
            <span style={{
              fontSize: '12px', fontWeight: 700, padding: '3px 10px', borderRadius: '8px',
              background: user?.role === 'radar_free' ? '#f1f5f9' : '#fef3c7',
              color: user?.role === 'radar_free' ? '#64748b' : '#d97706',
            }}>
              {ROLE_LABELS[user?.role] || 'Gratis'}
            </span>
          </div>
          {subscription ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f8fafc' }}>
                <span style={{ fontSize: '13px', color: '#64748b' }}>Bedrag</span>
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>{subscription.amount}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f8fafc' }}>
                <span style={{ fontSize: '13px', color: '#64748b' }}>Volgende betaling</span>
                <span style={{ fontSize: '13px', color: '#0f172a' }}>{subscription.next_payment || '—'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0' }}>
                <span style={{ fontSize: '13px', color: '#64748b' }}>Status</span>
                <span style={{ fontSize: '12px', fontWeight: 600, color: '#22c55e' }}>{subscription.status || 'Actief'}</span>
              </div>
            </>
          ) : (
            <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '10px' }}>
              <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '8px' }}>
                {user?.role === 'radar_free' ? 'U gebruikt momenteel het gratis plan.' : 'Betaalinformatie wordt beheerd via Stripe.'}
              </p>
              {user?.role === 'radar_free' && (
                <a href="/platform/checkout" style={{ fontSize: '13px', color: '#D4A017', fontWeight: 600, textDecoration: 'none' }}>
                  Upgrade naar IMPERIA AI Radar →
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
